import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheckIcon, DollarSignIcon, UsersIcon } from 'lucide-react';
import { TrackerState, User, LicenseData, MichiganLicenseData, StateComplianceData, US_STATES, DEFAULT_OFFICERS, createDefaultLicense, createDefaultMichiganLicense, createDefaultStateCompliance, getUserLicensedStateCount } from './types';
import { CompanyStateComplianceModal } from './CompanyStateComplianceModal';
import { UserLicenseModal } from './UserLicenseModal';
import { StateLicenseModal } from './StateLicenseModal';
import { StateModal } from './StateModal';
import { MichiganModal } from './MichiganModal';
import { LicenseMatrix } from './LicenseMatrix';
const STORAGE_KEY = 'nmls-license-tracker-v3';
const ORIGINAL_DEFAULT_OFFICER_NAMES: Record<string, string> = {
  'lo-01': 'Donovan Jappaya',
  'lo-02': 'Donovan Salem',
  'lo-03': 'Fadia Salem',
  'lo-04': 'Hayden Denha',
  'lo-05': 'Jaden Jappaya',
  'lo-06': 'Johnathan Denha',
  'lo-07': 'Lance Ibrahim',
  'lo-08': 'Max Fazio',
  'lo-09': 'Nikol Kadilli',
  'lo-10': 'Paul Shango',
  'lo-11': 'Saber Denha',
  'lo-12': 'Sarah Batayeh',
  'lo-13': 'Sharla Kalinowski'
};
function migrateDefaultOfficerNames(users: User[]): User[] {
  const namesById = new Map(DEFAULT_OFFICERS.map(officer => [officer.id, officer.name]));
  return users.map(user => {
    const nextName = namesById.get(user.id);
    const originalName = ORIGINAL_DEFAULT_OFFICER_NAMES[user.id];
    if (nextName && user.name === originalName) {
      return {
        ...user,
        name: nextName
      };
    }
    return user;
  });
}
function getInitialState(): TrackerState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as TrackerState;
      if (Array.isArray(parsed.users) && typeof parsed.licenses === 'object') {
        return {
          users: migrateDefaultOfficerNames(parsed.users),
          licenses: parsed.licenses || {},
          michiganLicenses: parsed.michiganLicenses || {},
          stateCompliance: parsed.stateCompliance || {}
        };
      }
    }
  } catch {
    // Fall through
  }
  return {
    users: DEFAULT_OFFICERS,
    licenses: {},
    michiganLicenses: {},
    stateCompliance: {}
  };
}
export function LicenseTracker() {
  const [state, setState] = useState<TrackerState>(getInitialState);
  // Modal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedStateName, setSelectedStateName] = useState<string | null>(null);
  // Detail modal: specific user + state
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [detailStateName, setDetailStateName] = useState<string | null>(null);
  // Company-level detail modal
  const [companyStateName, setCompanyStateName] = useState<string | null>(null);
  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);
  // --- License Updates ---
  const handleUpdateLicense = useCallback((userId: string, stateName: string, license: LicenseData) => {
    setState(prev => ({
      ...prev,
      licenses: {
        ...prev.licenses,
        [userId]: {
          ...(prev.licenses[userId] || {}),
          [stateName]: license
        }
      }
    }));
  }, []);
  const handleUpdateMichiganLicense = useCallback((userId: string, license: MichiganLicenseData) => {
    setState(prev => ({
      ...prev,
      michiganLicenses: {
        ...prev.michiganLicenses,
        [userId]: license
      }
    }));
  }, []);
  const handleUpdateStateCompliance = useCallback((stateName: string, compliance: StateComplianceData) => {
    setState(prev => ({
      ...prev,
      stateCompliance: {
        ...prev.stateCompliance,
        [stateName]: compliance
      }
    }));
  }, []);
  const handleAddUserToState = useCallback((userId: string, stateName: string) => {
    if (stateName === 'Michigan') {
      setState(prev => {
        if (prev.michiganLicenses[userId]) return prev;
        return {
          ...prev,
          michiganLicenses: {
            ...prev.michiganLicenses,
            [userId]: createDefaultMichiganLicense()
          }
        };
      });
    } else {
      setState(prev => {
        if (prev.licenses[userId]?.[stateName]) return prev;
        return {
          ...prev,
          licenses: {
            ...prev.licenses,
            [userId]: {
              ...(prev.licenses[userId] || {}),
              [stateName]: createDefaultLicense()
            }
          }
        };
      });
    }
  }, []);
  // --- Open detail for user+state ---
  const handleOpenUserStateDetail = useCallback((userId: string, stateName: string) => {
    setDetailUserId(userId);
    setDetailStateName(stateName);
  }, []);
  // --- Calculations ---
  const stats = useMemo(() => {
    let totalLicensed = 0;
    let totalInProgress = 0;
    let totalCost = 0;
    for (const user of state.users) {
      const counts = getUserLicensedStateCount(user.id, state.licenses, state.michiganLicenses);
      totalLicensed += counts.licensed;
      totalInProgress += counts.inProgress;
      // Sum costs from regular licenses
      const userLicenses = state.licenses[user.id] || {};
      for (const lic of Object.values(userLicenses)) {
        // Use charges if available, fall back to legacy cost field
        const chargesTotal = (lic.charges || []).reduce((a, c) => a + c.amount, 0);
        totalCost += chargesTotal > 0 ? chargesTotal : lic.cost || 0;
        totalCost += lic.renewals.reduce((a, r) => a + (r.fee || 0), 0);
      }
      // Sum costs from Michigan
      const mi = state.michiganLicenses[user.id];
      if (mi) {
        totalCost += mi.stepCosts.reduce((a, c) => a + (c || 0), 0);
        totalCost += mi.renewals.reduce((a, r) => a + (r.fee || 0), 0);
      }
    }
    return {
      totalLicensed,
      totalInProgress,
      totalCost,
      totalUsers: state.users.length
    };
  }, [state]);
  // --- Find user/license for detail modal ---
  const detailUser = detailUserId ? state.users.find(u => u.id === detailUserId) || null : null;
  const selectedUser = selectedUserId ? state.users.find(u => u.id === selectedUserId) || null : null;
  return <div className="h-screen w-full bg-[#0a0f1e] text-slate-200 flex flex-col overflow-hidden" data-id="element-111" data-mp="w0rp1a0mvrre">
      <div className="h-px w-full bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent flex-shrink-0" data-id="element-112" data-mp="i1jly61cnzhx" />

      <div className="flex flex-col flex-1 min-h-0 px-4 py-3" data-id="element-113" data-mp="myidcgtmpscf">
        {/* Header */}
        <header className="flex-shrink-0 mb-3" data-id="element-114" data-mp="32pxvufwvg7hx">
          <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-115" data-mp="32tbylp9g6ipw">
            {/* Stats Row */}
            <div className="flex flex-wrap items-center gap-2" data-id="element-116" data-mp="2z91q95jjxwn0">
              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-117" data-mp="2z7gw3h15gfpo">
                <UsersIcon className="w-3.5 h-3.5 text-slate-500" data-id="element-118" data-mp="2v9j98kuuq5pg" />
                <span className="text-xs text-slate-400" data-id="element-119" data-mp="395wqptzdy73x">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-120" data-mp="348ee9tm5t9r6">
                    {stats.totalUsers}
                  </span>{' '}
                  officers
                </span>
              </div>

              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-121" data-mp="2bk8rz2fgjcp8">
                <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]" data-id="element-122" data-mp="2q957tlnr9u0e" />
                <span className="text-xs text-slate-400" data-id="element-123" data-mp="2lg2enemw4mkq">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-124" data-mp="1xrj1olswblmb">
                    {stats.totalLicensed}
                  </span>{' '}
                  licensed
                </span>
              </div>

              {stats.totalInProgress > 0 && <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-125" data-mp="17c108j16kr6i">
                  <div className="w-2 h-2 rounded-full bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.8)]" data-id="element-126" data-mp="1h842rpzy2rtz" />
                  <span className="text-xs text-slate-400" data-id="element-127" data-mp="1m7gog7f2xsuz">
                    <span className="text-white font-bold text-sm tabular-nums" data-id="element-128" data-mp="1m4kn0940wm1s">
                      {stats.totalInProgress}
                    </span>{' '}
                    in progress
                  </span>
                </div>}

              <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-lg px-3 py-1.5" data-id="element-129" data-mp="1zzd9jribz2hv">
                <DollarSignIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-130" data-mp="24wvn5v7am5w7" />
                <span className="text-xs text-slate-400" data-id="element-131" data-mp="29xjao6wbc4lo">
                  <span className="text-white font-bold text-sm tabular-nums" data-id="element-132" data-mp="2dy371892b46r">
                    ${stats.totalCost.toLocaleString()}
                  </span>{' '}
                  invested
                </span>
              </div>
            </div>
          </div>
        </header>

        <LicenseMatrix users={state.users} licenses={state.licenses} michiganLicenses={state.michiganLicenses} stateCompliance={state.stateCompliance} onCellClick={handleOpenUserStateDetail} onCompanyStateClick={setCompanyStateName} onUserClick={setSelectedUserId} data-id="element-133" data-mp="3ugn3b885iy97" />
      </div>

      {/* --- Modals --- */}

      {/* Company Compliance Detail (click Olympian Mortgage state) */}
      {companyStateName && <CompanyStateComplianceModal stateName={companyStateName} isOpen={true} onClose={() => setCompanyStateName(null)} compliance={state.stateCompliance[companyStateName] || createDefaultStateCompliance()} onUpdateCompliance={compliance => {
      handleUpdateStateCompliance(companyStateName, compliance);
    }} data-id="element-134" data-mp="c73301sn20is" />}

      {/* User License Overview (click user → see all their states) */}
      {selectedUser && <UserLicenseModal user={selectedUser} isOpen={true} onClose={() => setSelectedUserId(null)} licenses={state.licenses[selectedUser.id] || {}} michiganLicense={state.michiganLicenses[selectedUser.id]} onOpenState={stateName => {
      handleOpenUserStateDetail(selectedUser.id, stateName);
    }} data-id="element-135" data-mp="376rtxclnplvd" />}

      {/* State License Overview (click state → see all users in that state) */}
      {selectedStateName && !selectedUserId && <StateLicenseModal stateName={selectedStateName} isOpen={true} onClose={() => setSelectedStateName(null)} users={state.users} licenses={state.licenses} michiganLicenses={state.michiganLicenses} onUpdateLicense={handleUpdateLicense} onUpdateMichiganLicense={handleUpdateMichiganLicense} onAddUserToState={handleAddUserToState} onOpenUserStateDetail={handleOpenUserStateDetail} data-id="element-136" data-mp="3kxwq24ptfw0y" />}

      {/* Detail: specific user + specific state */}
      {detailUser && detailStateName && detailStateName !== 'Michigan' && <StateModal user={detailUser} stateName={detailStateName} isOpen={true} onClose={() => {
      setDetailUserId(null);
      setDetailStateName(null);
    }} license={state.licenses[detailUser.id]?.[detailStateName] || createDefaultLicense()} onUpdateLicense={license => {
      handleUpdateLicense(detailUser.id, detailStateName!, license);
    }} data-id="element-137" data-mp="dc3zdgrb2qmm" />}

      {/* Detail: specific user + Michigan */}
      {detailUser && detailStateName === 'Michigan' && <MichiganModal user={detailUser} isOpen={true} onClose={() => {
      setDetailUserId(null);
      setDetailStateName(null);
    }} license={state.michiganLicenses[detailUser.id] || createDefaultMichiganLicense()} onUpdateLicense={license => {
      handleUpdateMichiganLicense(detailUser.id, license);
    }} data-id="element-138" data-mp="3gtm2jaw4v9gp" />}
    </div>;
}